<script>
  // import input formatting functions
  import {
    formatCurrency,
    formatMonths,
    formatPercentage,
    setCursorPosition,
  } from '../util';
  // calculate refinance savings function
  import calculateRefinanceSavings from './calculateRefinanceSavings';
  // import calculator base components
  import CalculatorDisclaimer from '../CalculatorDisclaimer.svelte';
  import CalculatorFieldError from '../CalculatorFieldError.svelte';
  import CalculatorHeader from '../CalculatorHeader.svelte';
  import CalculatorSubmitInput from '../CalculatorSubmitInput';

  // company prop for passing to Disclaimer
  export let company;

  // fields
  let originalInterestRate = {
    value: '',
    error: false,
    errorMessage: 'Please enter a value',
  };
  let originalLoanAmount = {
    value: '',
    error: false,
    errorMessage: 'Please enter a value',
  };
  let originalLoanTerm = {
    value: '30',
    error: false,
    errorMessage: 'Please select a loan term',
  };
  let timeLeftToPayOnOriginalLoan = {
    value: '',
    error: false,
    errorMessage: 'Please enter a value',
  };
  let newInterestRate = {
    value: '',
    error: false,
    errorMessage: 'Please enter a value',
  };
  let newLoanAmount = {
    value: '',
    error: false,
    errorMessage: 'Please enter a value',
  };
  let newLoanTerm = {
    value: '30',
    error: false,
    errorMessage: 'Please select a loan term',
  };

  // result values
  let oldLoanValues = {
    monthlyPayment: '',
    remainingLoanBalance: '',
    remainingTotalCost: '',
  };
  let newLoanValues = {
    monthlyPayment: '',
    monthlySavings: '',
    totalCost: '',
  };
  let totalSavings = '';

  // has the result been calculated?
  let calculated = false;

  // format percentage for originalInterestRate and update value
  function formatOriginalInterestRate() {
    // if no input entered, exit
    if (originalInterestRate.value === '') return;

    const formattedPercentage = formatPercentage(originalInterestRate.value);

    originalInterestRate.value = formattedPercentage;
  }

  // format percentage for newInterestRate and update value
  function formatNewInterestRate() {
    // if no input entered, exit
    if (newInterestRate.value === '') return;

    const formattedPercentage = formatPercentage(newInterestRate.value);

    newInterestRate.value = formattedPercentage;
  }

  // format months for timeLeftToPayOnOriginalLoan and update value
  function formatTimeLeftToPayOnOriginalLoan() {
    // if no input entered, reset to blank and return
    if (
      timeLeftToPayOnOriginalLoan.value === '' ||
      timeLeftToPayOnOriginalLoan.value === ' Months'
    ) {
      timeLeftToPayOnOriginalLoan.value = '';
      return;
    }

    const formattedMonths = formatMonths(timeLeftToPayOnOriginalLoan.value);

    timeLeftToPayOnOriginalLoan.value = formattedMonths;
  }

  // reactive statements to format input values when they change
  $: if (originalLoanAmount.value !== '')
    originalLoanAmount.value = formatCurrency(originalLoanAmount.value);

  $: if (newLoanAmount.value !== '')
    newLoanAmount.value = formatCurrency(newLoanAmount.value);

  // do required fields have a value?
  $: if (
    originalInterestRate.value !== '' &&
    originalInterestRate.value !== '0.000%'
  )
    originalInterestRate.error = false;

  $: if (originalLoanAmount.value !== '' && originalLoanAmount.value !== '$0')
    originalLoanAmount.error = false;

  $: if (
    timeLeftToPayOnOriginalLoan.value !== '' &&
    timeLeftToPayOnOriginalLoan.value !== '0 Months'
  )
    timeLeftToPayOnOriginalLoan.error = false;

  $: if (newInterestRate.value !== '' && newInterestRate.value !== '0.000%')
    newInterestRate.error = false;

  $: if (newLoanAmount.value !== '' && newLoanAmount.value !== '$0')
    newLoanAmount.error = false;

  function setCalculatedAndCalculate() {
    if (
      originalInterestRate.value === '' ||
      originalInterestRate.value === '0.000%'
    )
      originalInterestRate.error = true;

    if (originalLoanAmount.value === '' || originalLoanAmount.value === '$0')
      originalLoanAmount.error = true;

    if (
      timeLeftToPayOnOriginalLoan.value === '' ||
      timeLeftToPayOnOriginalLoan.value === '0 Months'
    )
      timeLeftToPayOnOriginalLoan.error = true;

    if (newInterestRate.value === '' || newInterestRate.value === '0.000%')
      newInterestRate.error = true;

    if (newLoanAmount.value === '' || newLoanAmount.value === '$0')
      newLoanAmount.error = true;

    if (
      originalInterestRate.value === '0.000%' ||
      originalInterestRate.value === '' ||
      originalLoanAmount.value === '$0' ||
      originalLoanAmount.value === '' ||
      timeLeftToPayOnOriginalLoan.value === '' ||
      timeLeftToPayOnOriginalLoan.value === '0 Months' ||
      newInterestRate.value === '0.000%' ||
      newInterestRate.value === '' ||
      newLoanAmount.value === '$0' ||
      newLoanAmount.value === ''
    )
      return;

    calculated = true;

    const calculatedRefinanceSavings = calculateRefinanceSavings(
      originalInterestRate.value,
      originalLoanAmount.value,
      originalLoanTerm.value,
      timeLeftToPayOnOriginalLoan.value,
      newInterestRate.value,
      newLoanAmount.value,
      newLoanTerm.value
    );

    oldLoanValues = calculatedRefinanceSavings[0];
    newLoanValues = calculatedRefinanceSavings[1];
    totalSavings = calculatedRefinanceSavings[2];
  }
</script>

<div>
  <CalculatorHeader
    title="Refinance Savings Calculator"
    subtitle="Calculate How Much You Will Save By Refinancing" />
  <form on:submit|preventDefault={setCalculatedAndCalculate}>
    <div class="bg-gray-efftwo px-16 py-8">
      <div class="calculator-input-fields-wrapper">
        <div class="calculator-field lg:mr-16 -mb-12 lg:mb-0 ">
          <label for="originalInterestRate" class="block">
            Original Interest Rate
          </label>
          <input
            type="text"
            id="originalInterestRate"
            name="originalInterestRate"
            placeholder="3.500%"
            on:blur={formatOriginalInterestRate}
            bind:value={originalInterestRate.value}
            class="mt-2" />
          <CalculatorFieldError field={originalInterestRate} />
        </div>
        <div class="calculator-field lg:mr-16 -mb-12 lg:mb-0 ">
          <label for="originalLoanAmount" class="block">
            Original Loan Amount
          </label>
          <input
            type="text"
            id="originalLoanAmount"
            name="originalLoanAmount"
            placeholder="$250,000"
            bind:value={originalLoanAmount.value}
            class="mt-2" />
          <CalculatorFieldError field={originalLoanAmount} />
        </div>
        <div class="calculator-field mb-12 lg:mb-0">
          <label for="originalLoanTerm" class="block">Original Loan Term</label>
          <select
            name="originalLoanTerm"
            id="originalLoanTerm"
            bind:value={originalLoanTerm.value}
            class="mt-2"
            required>
            <option value="30" selected>30 Years</option>
            <option value="25">25 Years</option>
            <option value="20">20 Years</option>
            <option value="15">15 Years</option>
            <option value="10">10 Years</option>
            <option value="5">5 Years</option>
          </select>
        </div>
        <div class="calculator-field lg:mr-16 -mb-12 lg:mb-0 ">
          <label for="timeLeftToPayOnOriginalLoan" class="block">
            Time Left to Pay on Original Loan
          </label>
          <input
            type="text"
            id="timeLeftToPayOnOriginalLoan"
            name="timeLeftToPayOnOriginalLoan"
            placeholder="240 Months"
            on:blur={formatTimeLeftToPayOnOriginalLoan}
            bind:value={timeLeftToPayOnOriginalLoan.value}
            use:setCursorPosition={' Months'}
            class="mt-2" />
          <CalculatorFieldError field={timeLeftToPayOnOriginalLoan} />
        </div>
        <div class="calculator-field lg:mr-16 -mb-12 lg:mb-0 ">
          <label for="newInterestRate" class="block">New Interest Rate</label>
          <input
            type="text"
            id="newInterestRate"
            name="newInterestRate"
            placeholder="3.250%"
            on:blur={formatNewInterestRate}
            bind:value={newInterestRate.value}
            class="mt-2" />
          <CalculatorFieldError field={newInterestRate} />
        </div>
        <div class="calculator-field lg:mr-16 -mb-12 lg:mb-0 ">
          <label for="newLoanAmount" class="block">New Loan Amount</label>
          <input
            type="text"
            id="newLoanAmount"
            name="newLoanAmount"
            placeholder="$150,000"
            bind:value={newLoanAmount.value}
            class="mt-2" />
          <CalculatorFieldError field={newLoanAmount} />
        </div>
        <div class="calculator-field">
          <label for="newLoanTerm" class="block">New Loan Term</label>
          <select
            name="newLoanTerm"
            id="newLoanTerm"
            bind:value={newLoanTerm.value}
            class="mt-2"
            required>
            <option value="30" selected>30 Years</option>
            <option value="25">25 Years</option>
            <option value="20">20 Years</option>
            <option value="15">15 Years</option>
            <option value="10">10 Years</option>
            <option value="5">5 Years</option>
          </select>
        </div>
      </div>
    </div>
    <CalculatorSubmitInput />
  </form>

  <div class="mt-10">
    {#if calculated}
      <div class="mt-10">
        <h3
          class="calculator-result-title text-white font-medium mb-0 py-8
          bg-black lg:font-normal">
          Old Loan
        </h3>
        <div class="bg-gray-efftwo px-16">
          <div class="lg:w-4/5">
            <div class="flex flex-col pt-10 pb-16 lg:flex-row lg:items-center">
              <div class="mb-20 lg:mb-0 lg:w-1/3">
                <p class="calculator-result-subtitle">Monthly Payment</p>
                <p class="calculator-result-result">
                  ${oldLoanValues.monthlyPayment}
                </p>
              </div>
              <div class="mb-20 lg:mb-0 lg:w-1/3">
                <p class="calculator-result-subtitle">Remaining Loan Balance</p>
                <p class="calculator-result-result">
                  ${oldLoanValues.remainingBalance}
                </p>
              </div>
              <div class="lg:w-1/3">
                <p class="calculator-result-subtitle">Remaining Total Cost</p>
                <p class="calculator-result-result">
                  ${oldLoanValues.remainingTotal}
                </p>
              </div>
            </div>
          </div>
        </div>
        <h3
          class="calculator-result-title text-white font-medium mb-0 py-8
          bg-black lg:font-normal">
          New Loan
        </h3>
        <div class="bg-gray-efftwo px-16 pb-8">
          <div class="pb-8 lg:w-4/5">
            <div class="flex flex-col pt-10 pb-8 lg:flex-row lg:items-center">
              <div class="mb-20 lg:mb-0 lg:w-1/3">
                <p class="calculator-result-subtitle">Monthly Payment</p>
                <p class="calculator-result-result">
                  ${newLoanValues.monthlyPayment}
                </p>
              </div>
              <div class="mb-20 lg:mb-0 lg:w-1/3">
                <p class="calculator-result-subtitle">Monthly Savings</p>
                <p class="calculator-result-result">
                  ${newLoanValues.monthlySavings}
                </p>
              </div>
              <div class="lg:w-1/3">
                <p class="calculator-result-subtitle">Remaining Total Cost</p>
                <p class="calculator-result-result">
                  ${newLoanValues.totalCost}
                </p>
              </div>
            </div>
            <div class="text-center pt-8">
              <p class="calculator-result-subtitle">Total Savings</p>
              <p class="calculator-result-result">${totalSavings}</p>
            </div>
          </div>
          <CalculatorDisclaimer {company} />
        </div>
      </div>
    {/if}
  </div>
</div>



// WEBPACK FOOTER //
// ./scripts/svelte/calculators/refinanceSavingsCalculator/RefinanceSavingsCalculator.svelte